/**
 * State store for floating panels.
 *
 * Panels are simply open or closed — a single Set<FloatingPanelId>.
 * Visibility in the UI is further gated by PANEL_ALLOWED_MODES so that
 * panels only render in workspace modes where they apply.
 *
 * Panels can live in the right-side dock (default) or float freely.
 * Open/closed and dock state are persisted to localStorage.
 */

import { useEffect } from "react";
import { create } from "zustand";
import { useStore } from "../app/store.ts";
import {
  DOCK_DEFAULT_WIDTH,
  DOCK_MAX_WIDTH,
  DOCK_MIN_WIDTH,
} from "./tokens.ts";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type FloatingPanelId =
  | "inspector"
  | "appearance"
  | "material"
  | "transform"
  | "modifiers"
  | "shapekeys"
  | "poseLibrary"
  | "handPoses"
  | "sketch"
  | "reference"
  | "layers"
  | "renderCamera"
  | "renderInking"
  | "renderOutput"
  | "vertexGroups"
  | "faceGroups"
  | "edgeData"
  | "inkingFeatures"
  | "inkingStrokes"
  | "inkingSaved";
export type EditorMode =
  | "object"
  | "edit"
  | "sculpt"
  | "sketch"
  | "pose"
  | "reference"
  | "render"
  | "inking";

interface FloatingPanelStore {
  /** Set of panels the user has open. */
  openPanels: Set<FloatingPanelId>;

  // -- Dock state ----------------------------------------------------------

  /** Panels living in the right-side dock (vs floating). */
  dockedPanels: Set<FloatingPanelId>;
  /** Whether the dock is collapsed to an icon strip. */
  dockCollapsed: boolean;
  /** Panels individually collapsed (accordion) within the dock. */
  dockPanelCollapsed: Set<FloatingPanelId>;

  // -- Temporary panel (collapsed-dock popover) ----------------------------

  /** When the dock is collapsed, the panel currently shown as a temporary popover. */
  temporaryPanel: FloatingPanelId | null;
  /** Whether the temporary panel was explicitly opened via click (pinned) or hover (transient). */
  temporaryPanelPinned: boolean;

  // -- Selection -----------------------------------------------------------

  selectedPanelKey: string | null;
  closeSelectedPanel: (() => void) | null;

  // -- Actions -------------------------------------------------------------

  toggle: (id: FloatingPanelId) => void;
  open: (id: FloatingPanelId) => void;
  close: (id: FloatingPanelId) => void;

  undockPanel: (id: FloatingPanelId) => void;
  dockPanel: (id: FloatingPanelId) => void;
  toggleDockCollapsed: () => void;
  toggleDockPanelCollapsed: (id: FloatingPanelId) => void;

  setTemporaryPanel: (id: FloatingPanelId | null, pinned?: boolean) => void;

  selectPanel: (key: string, closeFn: () => void) => void;
  clearSelection: () => void;
}

// ---------------------------------------------------------------------------
// Static configuration
// ---------------------------------------------------------------------------

/**
 * Panels that are permanently docked — always visible in the dock,
 * cannot be closed or undocked.
 */
export const ALWAYS_DOCKED_PANELS: ReadonlySet<FloatingPanelId> =
  new Set<FloatingPanelId>(["inspector"]);

export const PANEL_ALLOWED_MODES: Record<
  FloatingPanelId,
  readonly EditorMode[]
> = {
  inspector: [
    "object",
    "edit",
    "sculpt",
    "sketch",
    "pose",
    "reference",
    "render",
    "inking",
  ],
  appearance: ["object", "edit"],
  material: ["object", "edit"],
  transform: ["object"],
  modifiers: ["object"],
  shapekeys: ["object", "pose"],
  poseLibrary: ["object", "pose"],
  handPoses: ["object", "pose"],
  sketch: [],
  reference: ["reference"],
  layers: ["sketch", "reference"],
  renderCamera: ["render", "inking"],
  renderInking: ["inking"],
  renderOutput: ["render"],
  vertexGroups: ["edit"],
  faceGroups: ["edit"],
  edgeData: ["edit"],
  inkingFeatures: ["inking"],
  inkingStrokes: ["inking"],
  inkingSaved: ["inking"],
};

/**
 * Panels that should only be visible when a mannequin is selected
 * (or pose mode targets a mannequin).
 */
export const MANNEQUIN_ONLY_PANELS: ReadonlySet<FloatingPanelId> =
  new Set<FloatingPanelId>(["poseLibrary", "handPoses"]);

export const FLOATING_PANEL_IDS: readonly FloatingPanelId[] = [
  "inspector",
  "transform",
  "appearance",
  "material",
  "modifiers",
  "shapekeys",
  "poseLibrary",
  "handPoses",
  "layers",
  "sketch",
  "reference",
  "renderCamera",
  "renderInking",
  "renderOutput",
  "vertexGroups",
  "faceGroups",
  "edgeData",
  "inkingFeatures",
  "inkingStrokes",
  "inkingSaved",
];

// ---------------------------------------------------------------------------
// localStorage persistence
// ---------------------------------------------------------------------------

const STORAGE_KEY = "poseref-open-panels";
const DOCK_STATE_KEY = "poseref-dock-state";
const DOCK_WIDTH_KEY = "poseref-dock-width";

function loadOpenPanels(): Set<FloatingPanelId> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const validIds = new Set<string>(FLOATING_PANEL_IDS);
      return new Set(
        (JSON.parse(raw) as string[]).filter((id) =>
          validIds.has(id),
        ) as FloatingPanelId[],
      );
    }
  } catch {
    /* corrupted — start fresh */
  }
  return new Set();
}

function saveOpenPanels(openPanels: Set<FloatingPanelId>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...openPanels]));
  } catch {
    /* ignore */
  }
}

interface DockStateSnapshot {
  dockedPanels: FloatingPanelId[];
  collapsed: boolean;
  panelCollapsed: FloatingPanelId[];
}

function loadDockState(): {
  dockedPanels: Set<FloatingPanelId>;
  dockCollapsed: boolean;
  dockPanelCollapsed: Set<FloatingPanelId>;
} {
  const defaults = {
    dockedPanels: new Set<FloatingPanelId>(FLOATING_PANEL_IDS),
    dockCollapsed: false,
    dockPanelCollapsed: new Set<FloatingPanelId>(),
  };
  try {
    const raw = localStorage.getItem(DOCK_STATE_KEY);
    if (!raw) return defaults;
    const parsed: DockStateSnapshot = JSON.parse(raw);
    const validIds = new Set<string>(FLOATING_PANEL_IDS);
    return {
      dockedPanels: new Set(
        parsed.dockedPanels.filter((id) => validIds.has(id)),
      ),
      dockCollapsed: parsed.collapsed,
      dockPanelCollapsed: new Set(
        parsed.panelCollapsed.filter((id) => validIds.has(id)),
      ),
    };
  } catch {
    return defaults;
  }
}

function saveDockState(state: FloatingPanelStore): void {
  try {
    const snap: DockStateSnapshot = {
      dockedPanels: [...state.dockedPanels],
      collapsed: state.dockCollapsed,
      panelCollapsed: [...state.dockPanelCollapsed],
    };
    localStorage.setItem(DOCK_STATE_KEY, JSON.stringify(snap));
  } catch {
    /* ignore */
  }
}

/** Load persisted dock width, clamped to valid range. */
export function loadDockWidth(): number {
  try {
    const raw = localStorage.getItem(DOCK_WIDTH_KEY);
    if (raw)
      return Math.min(DOCK_MAX_WIDTH, Math.max(DOCK_MIN_WIDTH, Number(raw)));
  } catch {
    /* ignore */
  }
  return DOCK_DEFAULT_WIDTH;
}

export function saveDockWidth(width: number): void {
  try {
    localStorage.setItem(DOCK_WIDTH_KEY, String(width));
  } catch {
    /* ignore */
  }
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

const initialDock = loadDockState();

// Ensure all known panels are in the dock on startup. Persisted state may
// be stale (e.g. a panel was undocked then the page closed before it was
// re-docked). Since panels are always visible in the sidebar when allowed,
// they must always be in dockedPanels.
const initialOpenPanels = loadOpenPanels();
for (const id of FLOATING_PANEL_IDS) {
  initialDock.dockedPanels.add(id);
}
for (const id of ALWAYS_DOCKED_PANELS) {
  initialOpenPanels.add(id);
}

export const useFloatingPanelStore = create<FloatingPanelStore>((set) => ({
  openPanels: initialOpenPanels,
  dockedPanels: initialDock.dockedPanels,
  dockCollapsed: initialDock.dockCollapsed,
  dockPanelCollapsed: initialDock.dockPanelCollapsed,
  temporaryPanel: null,
  temporaryPanelPinned: false,
  selectedPanelKey: null,
  closeSelectedPanel: null,

  toggle: (id) =>
    set((state) => {
      // Always-docked panels cannot be toggled off
      if (ALWAYS_DOCKED_PANELS.has(id) && state.openPanels.has(id))
        return state;
      const next = new Set(state.openPanels);
      if (next.has(id)) {
        next.delete(id);
        const closing = state.selectedPanelKey === `fp-${id}`;
        return {
          openPanels: next,
          ...(closing
            ? { selectedPanelKey: null, closeSelectedPanel: null }
            : {}),
        };
      }
      next.add(id);
      return { openPanels: next };
    }),
  open: (id) =>
    set((state) => {
      if (state.openPanels.has(id)) return state;
      const next = new Set(state.openPanels);
      next.add(id);
      return { openPanels: next };
    }),
  close: (id) =>
    set((state) => {
      // Always-docked panels cannot be closed
      if (ALWAYS_DOCKED_PANELS.has(id)) return state;
      if (!state.openPanels.has(id)) return state;
      const nextOpen = new Set(state.openPanels);
      nextOpen.delete(id);
      // Re-dock the panel so it returns to the sidebar (collapsed) rather
      // than becoming permanently inaccessible.
      const nextDocked = new Set(state.dockedPanels);
      nextDocked.add(id);
      const nextCollapsed = new Set(state.dockPanelCollapsed);
      nextCollapsed.add(id);
      const closing = state.selectedPanelKey === `fp-${id}`;
      return {
        openPanels: nextOpen,
        dockedPanels: nextDocked,
        dockPanelCollapsed: nextCollapsed,
        ...(closing
          ? { selectedPanelKey: null, closeSelectedPanel: null }
          : {}),
      };
    }),

  undockPanel: (id) =>
    set((state) => {
      // Always-docked panels cannot be undocked
      if (ALWAYS_DOCKED_PANELS.has(id)) return state;
      if (!state.dockedPanels.has(id)) return state;
      const nextDocked = new Set(state.dockedPanels);
      nextDocked.delete(id);
      // Ensure the panel is in openPanels so the floating overlay renders it
      const nextOpen = new Set(state.openPanels);
      nextOpen.add(id);
      return { dockedPanels: nextDocked, openPanels: nextOpen };
    }),
  dockPanel: (id) =>
    set((state) => {
      if (state.dockedPanels.has(id)) return state;
      const next = new Set(state.dockedPanels);
      next.add(id);
      return { dockedPanels: next };
    }),
  toggleDockCollapsed: () =>
    set((state) => ({
      dockCollapsed: !state.dockCollapsed,
      temporaryPanel: null,
      temporaryPanelPinned: false,
    })),
  toggleDockPanelCollapsed: (id) =>
    set((state) => {
      const next = new Set(state.dockPanelCollapsed);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return { dockPanelCollapsed: next };
    }),

  setTemporaryPanel: (id, pinned = false) =>
    set((state) => {
      if (id === null)
        return { temporaryPanel: null, temporaryPanelPinned: false };

      if (state.temporaryPanel === id) {
        // Toggle off if already showing this panel AND it's a pinned click
        if (pinned && !state.temporaryPanelPinned) {
          return { temporaryPanelPinned: true };
        }
        if (pinned) {
          return { temporaryPanel: null, temporaryPanelPinned: false };
        }
        return state;
      }

      return { temporaryPanel: id, temporaryPanelPinned: pinned };
    }),

  selectPanel: (key, closeFn) =>
    set((state) => {
      if (state.selectedPanelKey === key) return state;
      return { selectedPanelKey: key, closeSelectedPanel: closeFn };
    }),
  clearSelection: () =>
    set({ selectedPanelKey: null, closeSelectedPanel: null }),
}));

// Persist on every change
useFloatingPanelStore.subscribe((state) => {
  saveOpenPanels(state.openPanels);
  saveDockState(state);
});

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/** Derive the current editor mode from the main app store's workspace mode. */
export function useEditorMode(): EditorMode {
  return useStore((s) => s.workspaceMode);
}

/**
 * Returns DraggablePanel selection props (`selected`, `onSelect`, `onClose`).
 * `panelKey` is the DraggablePanel `id` (e.g. "debug-overlay").
 * `closeFn` closes (toggles off) the panel.
 *
 * Also clears selection on unmount so that panels closed through external
 * means (e.g. the DebugMenu or mode switches) don't leave stale state.
 */
export function usePanelSelection(panelKey: string, closeFn: () => void) {
  const selectedPanelKey = useFloatingPanelStore((s) => s.selectedPanelKey);
  const selectPanel = useFloatingPanelStore((s) => s.selectPanel);

  useEffect(() => {
    return () => {
      if (useFloatingPanelStore.getState().selectedPanelKey === panelKey) {
        useFloatingPanelStore.getState().clearSelection();
      }
    };
  }, [panelKey]);

  return {
    selected: selectedPanelKey === panelKey,
    onSelect: () => selectPanel(panelKey, closeFn),
    onClose: closeFn,
  };
}
